import { z } from "zod";

/**
 * Defines a single rule in an approval chain, specifying which role
 * and how many members of that role are required to approve.
 */
export const approverRuleSchema = z.object({
  roleId: z.number(),
  roleName: z.string(),
  requiredApprovals: z
    .number()
    .int()
    .min(1, "At least one approver is required."),
});

/**
 * Defines the settings for a single action that can be gated by the approval system.
 */
export const approvalActionSchema = z.object({
  enabled: z.boolean(),
  approvers: z.array(approverRuleSchema),
});

/**
 * Defines the complete shape of the global approval settings object.
 * It uses a Zod record to allow for a dynamic, backend-driven list of actions.
 */
export const approvalSettingsSchema = z.object({
  isSystemEnabled: z.boolean(),
  actions: z.record(z.string(), approvalActionSchema),
  updatedAt: z.string().datetime().optional(),
});

const approvalVoteSchema = z.object({
  approverName: z.string(),
  roleId: z.number(),
  roleName: z.string(),
  approvedAt: z.string().datetime(),
  status: z.enum(["approved", "rejected"]),
  notes: z.string().nullable(),
});

export const approvalTaskSchema = z.object({
  id: z.number(),
  makerId: z.number(),
  makerName: z.string(),
  entityType: z.string(), // e.g., "Product", "AdminUser"
  entityKey: z.string(), // e.g., "robo_save", "123"
  entityName: z.string().nullable(), // e.g., Robo Save
  actionType: z.enum(["update", "create", "delete"]),
  status: z.enum(["pending", "approved", "rejected"]),
  proposedData: z.record(z.any()), // The JSON blob of proposed changes
  createdAt: z.string().datetime(),
});

export const approvalTaskDetailSchema = approvalTaskSchema.extend({
  currentData: z.record(z.any()).nullable(),
  approvals: z.array(approvalVoteSchema),
  approvalRules: z.array(
    z.object({
      roleId: z.number(),
      roleName: z.string(),
      requiredApprovals: z.number(),
    })
  ),
});

export const paginatedApprovalTasksResponseSchema = z.object({
  data: z.array(approvalTaskSchema),
  currentPage: z.number(),
  perPage: z.number(),
  total: z.number(),
  lastPage: z.number(),
});

export const approvalStatsSchema = z.object({
  totalPending: z.number().int(),
  approved: z.number().int(),
  rejected: z.number().int(),
  averageResponseTime: z.string(), // e.g., "5h 30m"
});

export const pendingApprovalResponseSchema = z.object({
  approvalPending: z.boolean(),
  taskId: z.number().optional(),
});

// --- Inferred TypeScript Types ---
export type ApproverRule = z.infer<typeof approverRuleSchema>;
export type ApprovalAction = z.infer<typeof approvalActionSchema>;
export type ApprovalSettings = z.infer<typeof approvalSettingsSchema>;
export type ApprovalTask = z.infer<typeof approvalTaskSchema>;
export type PaginatedApprovalTasksResponse = z.infer<
  typeof paginatedApprovalTasksResponseSchema
>;
export type ApprovalStats = z.infer<typeof approvalStatsSchema>;
export type ApprovalVote = z.infer<typeof approvalVoteSchema>;
export type ApprovalTaskDetail = z.infer<typeof approvalTaskDetailSchema>;
